import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { BRAND, waLink } from "../../brand";

const mainLinks = [
  { to: "/", label: "Inicio", end: true },
  { to: "/automatizacion-ia", label: "Automatización IA" },
  { to: "/respond-io", label: "Respond.io" },
];

const adsLinks = [
  { to: "/google-ads", label: "Google Ads", desc: "Intención · Leads · Performance" },
  { to: "/meta-ads", label: "Meta Ads", desc: "Facebook · Instagram · Remarketing" },
  { to: "/tiktok-ads", label: "TikTok Ads", desc: "Creatividad · Adquisición moderna" },
];

const tailLinks = [
  { to: "/funnels", label: "Funnels" },
  { to: "/casos-de-exito", label: "Casos de Éxito" },
  { to: "/contacto", label: "Contacto" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [adsOpen, setAdsOpen] = useState(false);
  const adsRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setOpen(false);
    setAdsOpen(false);
  }, [location.pathname]);

  // Close ads dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (adsRef.current && !adsRef.current.contains(e.target)) setAdsOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const adsActive = adsLinks.some((l) => l.to === location.pathname);

  const navLinkClass = ({ isActive }) =>
    `relative px-1.5 py-1 transition-colors text-[13px] font-display font-medium whitespace-nowrap ${
      isActive ? "text-white" : "text-zinc-400 hover:text-white"
    }`;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${scrolled ? "py-3" : "py-5"}`}
    >
      <div className="container-x">
        <nav
          className={`flex items-center justify-between rounded-full pl-3 pr-2 sm:pr-3 py-2 transition-all duration-300 ${
            scrolled ? "glass-strong glow-soft" : "bg-transparent border border-transparent"
          }`}
        >
          <Link to="/" className="flex items-center gap-2.5 pl-1.5" data-testid="logo-link">
            <img src={BRAND.logo} alt="Daos Ads" className="w-9 h-9 rounded-full object-cover ring-1 ring-white/10" />
            <div className="leading-tight">
              <div className="font-display font-bold text-white text-[15px]">Daos Ads</div>
              <div className="text-[10px] text-zinc-500 tracking-[0.18em] uppercase">Gana más, pierde cero</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <ul className="hidden xl:flex items-center gap-4 2xl:gap-6">
            {mainLinks.map((l) => (
              <li key={l.to}>
                <NavLink to={l.to} end={l.end} className={navLinkClass} data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}>
                  {({ isActive }) => (
                    <>
                      {l.label}
                      <span className={`pointer-events-none absolute inset-x-1.5 -bottom-1 h-px bg-gradient-to-r from-[#ff3b1f] to-[#ffd23f] transition-transform origin-left ${isActive ? "scale-x-100" : "scale-x-0"}`} />
                    </>
                  )}
                </NavLink>
              </li>
            ))}

            {/* Ads dropdown */}
            <li className="relative" ref={adsRef}>
              <button
                onClick={() => setAdsOpen((v) => !v)}
                data-testid="nav-link-publicidad"
                className={`relative px-1.5 py-1 transition-colors text-[13px] font-display font-medium inline-flex items-center gap-1 whitespace-nowrap ${
                  adsActive ? "text-white" : "text-zinc-400 hover:text-white"
                }`}
              >
                Publicidad <ChevronDown className={`w-3.5 h-3.5 transition-transform ${adsOpen ? "rotate-180" : ""}`} />
                <span className={`pointer-events-none absolute inset-x-1.5 -bottom-1 h-px bg-gradient-to-r from-[#ff3b1f] to-[#ffd23f] transition-transform origin-left ${adsActive ? "scale-x-100" : "scale-x-0"}`} />
              </button>
              {adsOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 glass-strong rounded-2xl p-2 glow-soft border border-white/10" data-testid="nav-ads-dropdown">
                  {adsLinks.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      className={({ isActive }) =>
                        `block rounded-xl px-3 py-2.5 transition ${isActive ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"}`
                      }
                      data-testid={`nav-link-${l.to.replace("/", "")}`}
                    >
                      <div className="text-white text-sm font-display font-semibold">{l.label}</div>
                      <div className="text-zinc-500 text-[11px]">{l.desc}</div>
                    </NavLink>
                  ))}
                </div>
              )}
            </li>

            {tailLinks.map((l) => (
              <li key={l.to}>
                <NavLink to={l.to} className={navLinkClass} data-testid={`nav-link-${l.to.replace("/", "")}`}>
                  {({ isActive }) => (
                    <>
                      {l.label}
                      <span className={`pointer-events-none absolute inset-x-1.5 -bottom-1 h-px bg-gradient-to-r from-[#ff3b1f] to-[#ffd23f] transition-transform origin-left ${isActive ? "scale-x-100" : "scale-x-0"}`} />
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2">
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              className="hidden md:inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white px-3 py-2 transition-colors font-display"
              data-testid="navbar-whatsapp-link"
            >
              <span className="w-2 h-2 rounded-full bg-[#25D366] pulse-dot" />
              WhatsApp
            </a>
            <Link to="/contacto" className="btn-primary !py-2.5 !px-5 text-[13.5px]" data-testid="navbar-cta-button">
              Agenda consultoría
            </Link>
            <button
              onClick={() => setOpen(!open)}
              className="xl:hidden ml-1 w-10 h-10 grid place-items-center rounded-full glass"
              aria-label="menu"
              data-testid="mobile-menu-toggle"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {open ? <path d="M6 6l12 12M6 18L18 6" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
              </svg>
            </button>
          </div>
        </nav>

        {open && (
          <div className="xl:hidden mt-2 glass-strong rounded-2xl p-3 flex flex-col gap-1" data-testid="mobile-menu">
            {[...mainLinks, ...adsLinks, ...tailLinks].map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-xl text-sm font-display ${
                    isActive ? "bg-white/[0.06] text-white" : "text-zinc-300 hover:text-white hover:bg-white/[0.03]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}
